import { apiFetch } from "../../services/api";
import { buildAgentChatThreadsUrl } from "../../utils/agentChatHistoryQuery";

/**
 * @param {number|object} [limitOrOpts]
 * @param {number} [limitOrOpts.limit]
 * @param {number} [limitOrOpts.offset]
 * @param {string|null} [limitOrOpts.last_message_after]
 * @param {string|null} [limitOrOpts.last_message_before]
 * @param {boolean} [limitOrOpts.include_archived]
 */
export async function listChatThreads(limitOrOpts = 50) {
  const opts =
    typeof limitOrOpts === "number"
      ? { limit: limitOrOpts }
      : limitOrOpts && typeof limitOrOpts === "object"
        ? limitOrOpts
        : {};
  const url = buildAgentChatThreadsUrl(opts);
  const res = await apiFetch(url);
  if (!res.ok) throw new Error("Failed to load chat history");
  return res.json();
}

export async function createChatThread(title = "New chat") {
  const res = await apiFetch("/api/agent/threads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error("Failed to create chat");
  return res.json();
}

export async function getChatThread(threadId) {
  const res = await apiFetch(`/api/agent/threads/${encodeURIComponent(threadId)}`);
  if (!res.ok) throw new Error("Failed to open chat");
  return res.json();
}

export async function archiveChatThread(threadId) {
  const res = await apiFetch(`/api/agent/threads/${encodeURIComponent(threadId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "archived" }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = data?.detail;
    throw new Error(
      typeof detail === "string"
        ? detail
        : detail?.message || "Failed to archive chat"
    );
  }
  return res.json();
}

export async function deleteChatThread(threadId) {
  const res = await apiFetch(`/api/agent/threads/${encodeURIComponent(threadId)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = data?.detail;
    throw new Error(
      typeof detail === "string"
        ? detail
        : detail?.message || `Failed to delete chat (${res.status})`
    );
  }
  return res.json();
}

export async function registerThreadAttachment(threadId, payload) {
  const res = await apiFetch(
    `/api/agent/threads/${encodeURIComponent(threadId)}/attachments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg =
      data?.detail?.message ||
      data?.detail ||
      "Failed to attach file";
    throw new Error(typeof msg === "string" ? msg : "Failed to attach file");
  }
  return res.json();
}

export async function deleteThreadAttachment(threadId, attachmentId) {
  const res = await apiFetch(
    `/api/agent/threads/${encodeURIComponent(threadId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { method: "DELETE" }
  );
  if (!res.ok) throw new Error("Failed to remove attachment");
  return res.json();
}

/**
 * Extract text via /api/upload-file then register on the thread.
 */
export async function uploadAndAttachToThread(threadId, file) {
  const formData = new FormData();
  formData.append("file", file);
  const uploadRes = await apiFetch("/api/upload-file", {
    method: "POST",
    body: formData,
  });
  if (!uploadRes.ok) {
    const data = await uploadRes.json().catch(() => ({}));
    const detail = data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : detail?.message ||
          (Array.isArray(detail) ? detail.map((d) => d.msg || d).join("; ") : null) ||
          "File extraction failed";
    throw new Error(msg);
  }
  const uploaded = await uploadRes.json();
  const extracted =
    uploaded.extracted_text || uploaded.preview || "";
  if (!String(extracted).trim()) {
    throw new Error("No text could be extracted from that file");
  }
  const registered = await registerThreadAttachment(threadId, {
    filename: uploaded.file_info?.filename || file.name,
    extracted_text: extracted,
    mime_type: file.type || uploaded.file_info?.file_type || null,
    byte_size: file.size || null,
  });
  return registered.attachment;
}

export function mapApiMessagesToUi(messages) {
  return (messages || []).map((m, idx) => ({
    id: m.id || `${m.created_at || idx}-${m.role}`,
    content: m.content || "",
    role: m.role,
    timestamp: m.created_at ? new Date(m.created_at) : new Date(),
    metadata: {
      ...(m.metadata || {}),
      turn_id: m.turn_id || undefined,
      skill_id: m.skill_id || undefined,
      fromHistory: true,
    },
  }));
}

export function contextFromThread(thread) {
  const ctx = thread?.context_json || {};
  return {
    activeEntity: ctx.active_entity || null,
    lastMentionedEntities: [],
    focusEntities: ctx.focus_entities || [],
    activeTask: ctx.active_task || null,
    suspendedTasks: ctx.suspended_tasks || [],
    workingMemory: ctx.working_memory || null,
    activeSkillId: ctx.active_skill_id || null,
  };
}
